import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        A GLB file is a 3D model saved in the binary version of <strong>glTF 2.0</strong>, the open 3D
        format from the Khronos Group. It packs everything a model needs (geometry, materials, textures,
        animations and the scene hierarchy) into one compact file, which is why GLB is the default 3D format
        for websites, AR and online stores. To open one, drag it into a free online viewer such as the
        Babylon.js Sandbox, or import it into Blender.
      </p>

      <h2>What does GLB stand for?</h2>
      <p>
        GLB is short for <strong>glTF Binary</strong>, and glTF stands for GL Transmission Format. Khronos calls glTF "the JPEG of 3D": a format built for delivering finished 3D
        assets efficiently rather than for editing them. glTF 2.0 is also an international standard,
        ISO/IEC 12113:2022.
      </p>
      <p>
        glTF comes in two packages: a <code>.gltf</code> file (readable JSON that points to separate geometry
        and texture files) and a <code>.glb</code> file (the same data in one binary file). The difference is
        covered in detail in <Link to="/blog/gltf-vs-glb">glTF vs GLB</Link>. The official MIME type for GLB
        is <code>model/gltf-binary</code>.
      </p>

      <h2>What's inside a GLB file</h2>
      <p>The <a href="https://registry.khronos.org/glTF/specs/2.0/glTF-2.0.html" target="_blank" rel="noopener noreferrer">glTF 2.0 specification</a> defines a simple layout:</p>
      <ol>
        <li>
          <strong>A 12-byte header.</strong> The magic bytes <code>glTF</code>, the container version (2) and
          the total file length.
        </li>
        <li>
          <strong>A JSON chunk.</strong> The scene description: nodes, meshes, materials, animations and
          pointers into the binary data. It's the same JSON you would find in a <code>.gltf</code> file.
        </li>
        <li>
          <strong>A binary (BIN) chunk.</strong> Raw vertex positions, normals, UVs, triangle indices,
          animation keyframes and usually the texture images themselves. This chunk is optional, but almost
          every real model has one.
        </li>
      </ol>
      <p>Inside that structure a GLB can hold:</p>
      <ul>
        <li><strong>Meshes</strong>: triangle geometry with normals, UVs, vertex colors and tangents.</li>
        <li><strong>PBR materials</strong>: metallic-roughness materials with base color, normal, occlusion and emissive maps.</li>
        <li><strong>Textures</strong>: usually PNG or JPEG; WebP, AVIF and KTX2 are possible through extensions.</li>
        <li><strong>Animations</strong>: keyframed movement, rotation and scale, plus several named clips per file.</li>
        <li><strong>Skins</strong>: skeletons and vertex weights for rigged characters.</li>
        <li><strong>Morph targets</strong>: blend shapes for facial expressions and other deformations.</li>
        <li><strong>Cameras and scene hierarchy</strong>: perspective or orthographic cameras and a tree of nodes with transforms.</li>
      </ul>
      <p>
        Extensions add more, such as punctual lights, extra material effects like clearcoat and transmission,
        and compressed geometry with <Link to="/blog/draco-compression">Draco</Link>. What a GLB does
        not store is editing data: modifiers, construction history or procedural shader networks are baked
        down or dropped when you export.
      </p>

      <h2>Where GLB files are used</h2>
      <ul>
        <li>
          <strong>3D on websites.</strong> three.js, Babylon.js and Google's <code>&lt;model-viewer&gt;</code> web
          component all load GLB directly. See <Link to="/blog/threejs-load-glb">how to load a GLB in three.js</Link>.
        </li>
        <li>
          <strong>Augmented reality.</strong> Android's Scene Viewer displays glTF/GLB models, and Google
          recommends keeping them under 10 MB. On iPhone, AR Quick Look uses USDZ instead, though{' '}
          <code>&lt;model-viewer&gt;</code> can generate the USDZ from your GLB on the fly.
        </li>
        <li>
          <strong>E-commerce.</strong> Shopify accepts GLB (and USDZ) for 3D product media and creates the other
          format automatically. More in <Link to="/blog/shopify-3d-models">3D models on Shopify</Link>.
        </li>
        <li>
          <strong>Games and real-time apps.</strong> Godot recommends glTF 2.0 as its 3D scene format, and most
          other engines can import it.
        </li>
        <li>
          <strong>Scanning and generation.</strong> Many 3D scanning apps and AI model generators export GLB
          because it's a single file that opens almost everywhere.
        </li>
      </ul>

      <h2>How to open a GLB file</h2>
      <p>
        Current versions of Windows and macOS have no built-in GLB viewer, but every platform has free
        options:
      </p>
      <table>
        <thead>
          <tr><th>Platform</th><th>Free ways to open a GLB</th></tr>
        </thead>
        <tbody>
          <tr><td>Any browser</td><td>Babylon.js Sandbox, three.js editor, &lt;model-viewer&gt; editor, glTF Viewer</td></tr>
          <tr><td>Windows</td><td>Babylon.js Sandbox (Microsoft's suggested replacement for 3D Viewer), Blender, F3D</td></tr>
          <tr><td>macOS</td><td>Blender, F3D, Khronos glTF Viewer (Apple silicon), an online viewer</td></tr>
          <tr><td>Linux</td><td>Blender, F3D, an online viewer</td></tr>
          <tr><td>iPhone and iPad</td><td>Khronos glTF Viewer app, an online viewer</td></tr>
          <tr><td>Android</td><td>An online viewer, F3D Android build</td></tr>
        </tbody>
      </table>

      <h3>Online GLB viewers</h3>
      <p>These work on any operating system:</p>
      <ul>
        <li>
          <a href="https://sandbox.babylonjs.com/" target="_blank" rel="noopener noreferrer">Babylon.js Sandbox</a>:
          drag and drop a <code>.glb</code> or <code>.gltf</code>. It plays animations and has an inspector for
          meshes and materials.
        </li>
        <li>
          <a href="https://threejs.org/editor/" target="_blank" rel="noopener noreferrer">three.js editor</a>:
          use File &gt; Import. You can move objects around and export again as GLB, glTF, OBJ, STL or USDZ.
        </li>
        <li>
          <a href="https://modelviewer.dev/editor/" target="_blank" rel="noopener noreferrer">&lt;model-viewer&gt; editor</a>:
          previews the model the way it will look in Google's web component. According to the editor, it
          doesn't send imported files to servers, except when you deploy to your phone for an AR test.
        </li>
        <li>
          <a href="https://gltf-viewer.donmccurdy.com/" target="_blank" rel="noopener noreferrer">glTF Viewer</a> by
          Don McCurdy: drop in a GLB, or a whole folder for a <code>.gltf</code> with its textures.
        </li>
      </ul>
      <p>
        To see what's inside the file rather than just how it looks, use our free{' '}
        <Link to="/gltf-validator">glTF validator</Link>. It runs the official Khronos glTF-Validator in your
        browser and lists errors, warnings, vertex and triangle counts, draw calls and the extensions the file uses.
      </p>

      <h3>Windows</h3>
      <p>
        For years, Windows opened GLB files in <strong>3D Viewer</strong>. Microsoft deprecated it in February
        2026 and removed it from the Microsoft Store on July 1, 2026. Existing installs keep working, but you
        can't download it anymore, and Microsoft now points people to the Babylon.js Sandbox. For a desktop app, install <a href="https://www.blender.org/download/" target="_blank" rel="noopener noreferrer">Blender</a> or
        the lightweight open-source viewer <a href="https://f3d.app/" target="_blank" rel="noopener noreferrer">F3D</a>, which can also be installed with winget.
      </p>

      <h3>macOS</h3>
      <p>
        Quick Look and Preview don't show GLB files; Apple's built-in 3D preview covers USDZ. Your options are
        Blender, F3D, Khronos' free open-source <strong>glTF Viewer</strong> app (which also runs on Macs with
        Apple silicon), a third-party Quick Look extension, or any online viewer.
      </p>

      <h3>Linux</h3>
      <p>Blender and F3D both have Linux builds, and the online viewers work in any modern browser.</p>

      <h3>iPhone, iPad and Android</h3>
      <p>
        On iOS, the <a href="https://apps.apple.com/us/app/khronos-gltf-viewer/id6470311948" target="_blank" rel="noopener noreferrer">Khronos glTF Viewer</a> opens
        GLB and glTF files from the Files app and can place them in AR. On Android, the simplest route is an
        online viewer in Chrome; F3D also publishes Android builds on its download page. Android's Scene
        Viewer is launched from websites and apps rather than from the file manager.
      </p>

      <h2>How to edit a GLB file</h2>
      <p>
        <strong>Blender</strong> is the standard free editor for GLB. Import it with File &gt; Import &gt; glTF 2.0
        (.glb, .gltf), make your changes, then use File &gt; Export &gt; glTF 2.0 and choose the glTF Binary (.glb)
        format. Use Principled BSDF materials, since that's what the exporter converts
        to glTF materials.
      </p>
      <p>
        For quick changes, the three.js editor can import and re-export GLB in the browser. For scripted edits, the open-source{' '}
        <Link to="/blog/gltf-transform-optimize">gltf-transform</Link> CLI works on GLB files directly.
      </p>

      <h2>How to convert a GLB file</h2>
      <h3>GLB to glTF and back</h3>
      <p>
        To pack a <code>.gltf</code> plus its <code>.bin</code> and textures into one GLB, use our free{' '}
        <Link to="/gltf-to-glb">glTF to GLB converter</Link>. It runs in your browser, nothing is uploaded and
        no sign-up is needed. To unpack a GLB into an editable <code>.gltf</code>, use the gltf-transform CLI:
      </p>
      <CodeBlock language="bash">{`npx @gltf-transform/cli copy model.glb model.gltf`}</CodeBlock>
      <p>
        That writes <code>model.gltf</code> plus a <code>.bin</code> file and the texture images next to it.
        Blender can do the same by exporting with the glTF Separate format.
      </p>

      <h3>GLB to USDZ (for iPhone AR)</h3>
      <p>
        Apple's Reality Converter used to be the usual tool for this, but Apple no longer lists it on its
        developer tools page. Current options:
      </p>
      <ul>
        <li>
          <strong>Blender</strong>: import the GLB, then export with the USD exporter (File &gt; Export &gt;
          Universal Scene Description) and give the file a <code>.usdz</code> extension. Blender then writes a
          USDZ archive with the textures included.
        </li>
        <li><strong>three.js editor</strong>: File &gt; Import your GLB, then File &gt; Export &gt; USDZ.</li>
        <li>
          <strong>Skip the conversion</strong>: <code>&lt;model-viewer&gt;</code> generates a USDZ for iOS AR
          automatically, and Shopify converts uploaded GLBs for you.
        </li>
      </ul>

      <h3>GLB to OBJ, FBX or STL</h3>
      <p>
        Import the GLB into Blender and use File &gt; Export with the OBJ, FBX or STL exporter. Keep in mind what
        each format can hold: OBJ keeps geometry and basic materials but drops animation and rigs, FBX keeps
        animation, and STL is geometry only, which is what 3D printers need.
      </p>

      <h2>Why GLB files get large, and how to make them smaller</h2>
      <p>The usual reasons a GLB ends up tens of megabytes:</p>
      <ul>
        <li><strong>Textures.</strong> A few 4K PNG maps can outweigh all the geometry in the file. This is the most common cause.</li>
        <li><strong>Uncompressed geometry.</strong> Positions, normals and UVs are usually stored as full 32-bit floats.</li>
        <li><strong>High polygon counts.</strong> Scans, CAD exports and AI-generated models can carry millions of triangles.</li>
        <li><strong>Leftovers.</strong> Duplicate vertices and unused materials, textures or objects from the source scene.</li>
      </ul>
      <p>
        The fixes: resize textures and re-encode them as WebP or AVIF, compress geometry with
        Draco, remove duplicate and unused data, and simplify meshes that are denser than they need to be.
        The full walkthrough is in <Link to="/blog/reduce-glb-file-size">how to reduce GLB file size</Link>.
      </p>
      <Callout title="Compress a GLB online">
        Our <Link to="/">GLB compressor</Link> does all of the above in one pass: Draco compression, texture
        resizing and WebP/AVIF conversion, plus cleanup and simplification passes. Typical results are
        60–90% smaller, and every output is checked with the Khronos validator. New accounts get one free
        compression.
      </Callout>

      <h2>FAQ</h2>

      <h3>Is GLB the same as glTF?</h3>
      <p>
        Same format, different packaging. A <code>.glb</code> is one binary file; a <code>.gltf</code> is JSON
        that usually references separate <code>.bin</code> and image files. You can convert between them
        without losing anything.
      </p>

      <h3>How do I open a GLB file on Windows 11?</h3>
      <p>
        Windows 11 has no built-in GLB viewer now that 3D Viewer has been removed from the Microsoft Store.
        Drag the file into the Babylon.js Sandbox in your browser, or install Blender or F3D.
      </p>

      <h3>Can a GLB file contain animations?</h3>
      <p>
        Yes. A GLB can store several named animation clips, skeletal rigs with skin weights, and morph target
        (blend shape) animation. Viewers like the Babylon.js Sandbox can play them back.
      </p>

      <h3>Can I 3D print a GLB file?</h3>
      <p>
        Usually you convert it first, since slicers are built around STL and 3MF. Import the GLB into Blender,
        check that the mesh is closed and at the right scale, and export it as STL.
      </p>

      <h3>How big should a GLB file be for the web?</h3>
      <p>
        As small as you can get it without visible quality loss. Google recommends staying under 10 MB for
        Android's Scene Viewer, and product viewers feel much faster at a few megabytes.
      </p>
    </>
  );
}
